import { FC, useCallback } from 'react';
import { useField } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import AsyncSelect, { AsyncSelectProps } from 'components/AsyncSelect';
import MultiSelectChip from 'components/MultiSelectChip';
import MultiSelectMenuItem from 'components/MultiSelectMenuItem';

import {
  TermsSelect_data$data,
  TermsSelect_data$key,
} from '__generated__/TermsSelect_data.graphql';
import Query from '__generated__/TermsSelectRefetchQuery.graphql';

export type Option = {
  id: string;
  name: string;
  icon: string | null;
};

type Props = Omit<
  AsyncSelectProps<Option>,
  'fragment' | 'getOptions' | 'query'
> & {
  data: TermsSelect_data$key;
  name: string;
};

const getOptions = (data: TermsSelect_data$data) =>
  data.terms?.edges.map(({ node }) => node) ?? [];

const optionEqual = (option: Option, value: Option) => option.id === value.id;

const TermsSelect: FC<Props> = ({ name, ...props }) => {
  const [{ value }, meta, { setValue }] = useField(name);
  const hasError = Boolean(meta.touched && meta.error);

  const handleChange = useCallback(
    (_, value) => {
      setValue(value);
    },
    [setValue]
  );

  return (
    <AsyncSelect<Option>
      multiple
      query={Query}
      isOptionEqualToValue={optionEqual}
      getOptions={getOptions}
      value={value}
      onChange={handleChange}
      error={hasError}
      helperText={hasError && meta.error}
      renderTags={(value, getTagProps) =>
        value.map((tag, index) => (
          <MultiSelectChip
            key={index}
            option={tag}
            getTagProps={getTagProps}
            index={index}
          />
        ))
      }
      renderOption={(props, option, { selected }) => (
        <MultiSelectMenuItem
          {...props}
          name={option.name}
          icon={option.icon}
          selected={selected}
          value={option.id}
        />
      )}
      fragment={graphql`
        fragment TermsSelect_data on Query
        @argumentDefinitions(search: { type: "String" })
        @refetchable(queryName: "TermsSelectRefetchQuery") {
          terms(first: 10, search: $search) {
            edges {
              node {
                id
                name
                icon
              }
            }
          }
        }
      `}
      {...props}
    />
  );
};

export default TermsSelect;
